import React, { useState, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { toast } from 'react-toastify';

const SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-000000000001';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [sending, setSending] = useState(false);
  const [formTime] = useState(() => Date.now());
  const captchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!captchaToken) {
      toast.error('Complete a verificação de segurança.');
      return;
    }
    setSending(true);
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001';
    fetch(`${API_BASE}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...formData,
        website: '',
        form_time: formTime,
        captcha_token: captchaToken,
      }),
    })
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          const msg = body.error || (body.errors && body.errors[0]?.msg) || 'Erro ao enviar.';
          throw new Error(msg);
        }
        toast.success('Mensagem enviada com sucesso! Em breve entraremos em contato.');
        setFormData({ name: '', email: '', message: '' });
        setCaptchaToken(null);
        captchaRef.current?.resetCaptcha();
      })
      .catch(err => {
        toast.error(err.message || 'Erro ao enviar mensagem. Tente novamente.');
        captchaRef.current?.resetCaptcha();
        setCaptchaToken(null);
      })
      .finally(() => setSending(false));
  };

  return (
    <section id="contato" className="py-24 bg-gray-50">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left: info panel */}
            <div
              className="lg:w-2/5 p-10 flex flex-col justify-between"
              style={{ background: '#102a4c' }}
            >
              <div>
                <div className="inline-flex items-center gap-2.5 mb-6">
                  <span className="w-1 h-4" style={{ background: '#F47920' }} />
                  <span className="text-xs font-bold uppercase tracking-widest text-blue-200">
                    Fale Conosco
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight tracking-tight">
                  Vamos transformar sua gestão juntos?
                </h2>
                <p className="text-blue-200/70 text-sm leading-relaxed mb-10">
                  Preencha o formulário e nossa equipe entrará em contato para apresentar a solução ideal para sua organização.
                </p>
              </div>
              <div className="space-y-5">
                {[
                  { icon: 'M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884zM18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z', text: 'gestao@carnaubahub.com' },
                  { icon: 'M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z', text: '(86) 98170-4823' },
                  { icon: 'M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z', text: 'Teresina, Piauí' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                        <path d={item.icon} fillRule="evenodd" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-blue-100 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:w-3/5 p-10">
              <h3 className="text-xl font-extrabold text-gray-900 mb-6">Envie uma mensagem</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* honeypot: hidden from humans, bots fill it → rejected by backend */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                  <input type="text" name="website" tabIndex="-1" autoComplete="off" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow bg-gray-50"
                    placeholder="Seu nome"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow bg-gray-50"
                    placeholder="seu.email@exemplo.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Mensagem
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow bg-gray-50 resize-none"
                    placeholder="Como podemos ajudar?"
                    required
                  />
                </div>
                <HCaptcha
                  sitekey={SITE_KEY}
                  onVerify={token => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken(null)}
                  ref={captchaRef}
                  theme="light"
                />
                <button
                  type="submit"
                  disabled={sending || !captchaToken}
                  className="w-full text-white font-bold px-8 py-4 rounded-lg shadow-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                  style={{ background: '#F47920' }}
                  onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#d96a10'; }}
                  onMouseLeave={e => e.currentTarget.style.background = '#F47920'}
                >
                  {sending ? 'Enviando...' : 'Enviar Mensagem'}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  Ao enviar, você concorda com nossa{' '}
                  <a href="/privacidade" className="underline hover:text-gray-600">Política de Privacidade</a>.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
